import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import type { Invoice } from '../hooks/useInvoices'
import { invoiceCategoryLabel } from '../lib/invoiceCategory'
import { STATUS_COLORS, fmt, fmtDate } from '../lib/invoiceFormat'
import { InvoicePDF } from './Invoice/InvoicePDF'
import { fetchInvoicePDFData } from './Invoice/fetchInvoicePDFData'
import { useAutosaveText } from '../hooks/useAutosaveText'
import AutosaveStatusLabel from './AutosaveStatusLabel'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../api/supabase'
import { canCredit, canEdit, canVoid, isCreditNote, syncedToSageBlocksVoid } from '../lib/invoiceRules'
import VoidInvoiceDialog from './Invoice/VoidInvoiceDialog'
import CreditNoteDialog from './Invoice/CreditNoteDialog'
import EditInvoiceDialog from './Invoice/EditInvoiceDialog'
import InvoiceHistory from './Invoice/InvoiceHistory'

// Extracted from Accounts.tsx (Z-nav fix, Gary 2026-09-28): rendered as an in-place modal from
// both the Accounts list AND the Job Charges tab, so "View Invoice" from a job never navigates
// away from it -- see feedback on losing your place after a full-page jump to /accounts.
export default function InvoiceDetailModal({ invoice, onClose, onStatusChange, onNotesChange, onChanged }: {
  invoice: Invoice
  onClose: () => void
  onStatusChange: (id: string, status: string) => Promise<void>
  onNotesChange: (id: string, notes: string) => Promise<void>
  // Called after a Void / Credit Note / Edit so the host list or job re-fetches.
  onChanged?: () => void | Promise<void>
}) {
  const { userRoles } = useAuth()
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'details' | 'history'>('details')
  const [dialog, setDialog] = useState<'void' | 'credit' | 'edit' | null>(null)
  const [related, setRelated] = useState<{ original?: { id: string; invoice_no: string } | null; credits: { id: string; invoice_no: string; total_amount: number; status: string }[] }>({ credits: [] })

  useEffect(() => {
    (async () => {
      if (isCreditNote(invoice) && invoice.credit_note_of_invoice_id) {
        const { data } = await supabase.from('invoices').select('id, invoice_no').eq('id', invoice.credit_note_of_invoice_id).maybeSingle()
        setRelated({ original: data, credits: [] })
      } else {
        const { data } = await supabase.from('invoices').select('id, invoice_no, total_amount, status').eq('credit_note_of_invoice_id', invoice.id).order('created_at')
        setRelated({ credits: (data as any[]) || [] })
      }
    })()
  }, [invoice.id, invoice.credit_note_of_invoice_id, invoice.status])

  const afterAction = async () => {
    setDialog(null)
    await onChanged?.()
    onClose()
  }
  const [pdfBusy, setPdfBusy] = useState<'download' | 'preview' | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  // Purely descriptive text, never affects amounts -- editable regardless of DRAFT/ISSUED/VOID,
  // unlike the Sales-side money fields on a charge which lock once invoiced. Autosaves via the
  // shared useAutosaveText hook (same as Customer reminder); the Save button stays as an
  // optional "save now", it's just no longer the only thing that persists the text.
  const notes = useAutosaveText(invoice.notes || '', v => onNotesChange(invoice.id, v.trim()))

  // Issue is the only status change left here -- Void and Credit go through their own dialogs.
  const handleStatus = async (status: string) => {
    setSaving(true)
    await onStatusChange(invoice.id, status)
    setSaving(false)
    onClose()
  }

  const buildPDF = async () => {
    const data = await fetchInvoicePDFData(invoice.id)
    return pdf(<InvoicePDF data={data} />).toBlob()
  }

  const handleDownloadPDF = async () => {
    setPdfBusy('download')
    setPdfError(null)
    try {
      const blob = await buildPDF()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${invoice.invoice_no}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to generate PDF')
    } finally {
      setPdfBusy(null)
    }
  }

  const handlePreviewPDF = async () => {
    setPdfBusy('preview')
    setPdfError(null)
    try {
      const blob = await buildPDF()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      // Deliberately not revoked immediately -- the new tab is still reading it; the browser
      // frees it when that tab is closed/navigated away.
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to generate PDF')
    } finally {
      setPdfBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{invoice.invoice_no}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {invoice.customer?.name}
              {invoice.job && (
                <> · Job <Link to={`/jobs/${invoice.job.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{invoice.job.job_ref}</Link></>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex gap-4 px-6 border-b border-gray-200 dark:border-gray-700 text-sm">
          {(['details', 'history'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`py-2 -mb-px border-b-2 capitalize ${tab === t ? 'border-blue-600 text-blue-700 dark:text-blue-400 font-medium' : 'border-transparent text-gray-500 dark:text-gray-400'}`}>{t}</button>
          ))}
        </div>

        {tab === 'history' && <div className="p-6"><InvoiceHistory invoiceId={invoice.id} /></div>}

        {tab === 'details' && <div className="p-6 space-y-6">
          {/* Summary grid */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Status', value: (
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-700'}`}>
                  {invoice.status}
                </span>
              )},
              { label: 'Category',       value: invoiceCategoryLabel(invoice.invoice_category) || '—' },
              { label: 'Invoice Date',   value: fmtDate(invoice.invoice_date) },
              { label: 'Due Date',       value: fmtDate(invoice.due_date) },
              { label: 'Currency',       value: invoice.currency },
              { label: 'Amount',         value: fmt(invoice.amount || 0, invoice.currency) },
              { label: 'VAT',            value: fmt(invoice.vat_amount || 0, invoice.currency) },
              { label: 'Total',          value: <span className="text-lg font-bold text-gray-900 dark:text-white">{fmt(invoice.total_amount || 0, invoice.currency)}</span> },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                <div className="text-sm text-gray-800 dark:text-gray-200">{value}</div>
              </div>
            ))}
          </div>

          {isCreditNote(invoice) && related.original && (
            <p className="text-sm text-gray-700 dark:text-gray-300">Credit against invoice <b>{related.original.invoice_no}</b>{invoice.credit_reason && <> — {invoice.credit_reason}</>}</p>
          )}
          {related.credits.length > 0 && (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              Credited {fmt(related.credits.filter(c => c.status !== 'VOID').reduce((s, c) => s + (c.total_amount || 0), 0), invoice.currency)} →{' '}
              {related.credits.map(c => c.invoice_no).join(', ')}
            </p>
          )}
          {invoice.status === 'VOID' && invoice.void_reason && (
            <p className="text-sm text-gray-600 dark:text-gray-400">Voided by {invoice.voided_by} on {fmtDate(invoice.voided_at)} — {invoice.void_reason}</p>
          )}

          {pdfError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg p-3">{pdfError}</div>
          )}

          <div className="flex flex-wrap gap-2">
            <button onClick={handleDownloadPDF} disabled={pdfBusy !== null}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              {pdfBusy === 'download' ? 'Preparing…' : 'Download PDF'}
            </button>
            <button onClick={handlePreviewPDF} disabled={pdfBusy !== null}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              {pdfBusy === 'preview' ? 'Preparing…' : 'Preview'}
            </button>
          </div>

          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Notes</p>
            <textarea rows={3} value={notes.value} onChange={e => notes.onChange(e.target.value)}
              placeholder="Descriptive text only -- never affects amounts."
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white text-sm" />
            <div className="flex items-center gap-2 mt-1.5">
              <button onClick={notes.saveNow} disabled={notes.status === 'saving'}
                className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Save Notes
              </button>
              <AutosaveStatusLabel status={notes.status} />
            </div>
          </div>

          {/* Corrections: Credit Note is the main route for an issued invoice; Void is restricted
              (DRAFT, or ISSUED but never synced to Sage, ADMIN only) -- see lib/invoiceRules.ts. */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Actions</p>
            <div className="flex flex-wrap gap-2">
              {invoice.status === 'DRAFT' && (
                <button onClick={() => handleStatus('ISSUED')} disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  Issue Invoice
                </button>
              )}
              {canCredit(invoice, userRoles) && (
                <button onClick={() => setDialog('credit')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-sm">
                  Create Credit Note
                </button>
              )}
              {canEdit(invoice, userRoles) && (
                <button onClick={() => setDialog('edit')}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700">
                  Edit
                </button>
              )}
              {canVoid(invoice, userRoles) && (
                <button onClick={() => setDialog('void')}
                  className="px-3 py-2 border border-red-300 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50">
                  Void Invoice
                </button>
              )}
            </div>
            {invoice.status === 'ISSUED' && !isCreditNote(invoice) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                <b>Credit Note</b> = the invoice was sent; offset it with a credit note. <b>Void</b> = treat it as never issued (only before it is sent / synced to Sage).
              </p>
            )}
            {syncedToSageBlocksVoid(invoice, userRoles) && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">This invoice has been synced to Sage — raise a Credit Note to reverse it.</p>
            )}
          </div>
        </div>}
      </div>

      {dialog === 'void' && <VoidInvoiceDialog invoice={invoice} onClose={() => setDialog(null)} onDone={afterAction} />}
      {dialog === 'credit' && <CreditNoteDialog invoice={invoice} onClose={() => setDialog(null)} onDone={afterAction} />}
      {dialog === 'edit' && <EditInvoiceDialog invoice={invoice} onClose={() => setDialog(null)} onDone={afterAction} />}
    </div>
  )
}
